import {useCallback} from "react";
import {MapPin, Calendar} from "lucide-react";
import * as m from "@/paraglide/messages";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useLocations,
  type PersonasFilterState,
  DEFAULT_FILTER_STATE,
} from "@/hooks/use-ai-personas";

interface MainFiltersProps {
  filters: PersonasFilterState;
  onFiltersChange: (filters: PersonasFilterState) => void;
}

export function MainFilters({filters, onFiltersChange}: MainFiltersProps) {
  const {data: locations, isLoading: locationsLoading} = useLocations();

  const handleLocationChange = useCallback(
    (value: string) => {
      onFiltersChange({
        ...filters,
        locationId: value === "all" ? null : value,
        date: DEFAULT_FILTER_STATE.date, // Reset date to default when location changes
      });
    },
    [filters, onFiltersChange]
  );

  const handleDateChange = useCallback(
    (value: string) => {
      onFiltersChange({
        ...filters,
        date: value === "all" ? null : value,
      });
    },
    [filters, onFiltersChange]
  );

  return (
    <div className="bg-card/50 border-border/50 rounded-xl border p-4 backdrop-blur-sm">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:gap-6">
        {/* Location Filter */}
        <div className="flex-1 space-y-2">
          <label className="text-muted-foreground flex items-center gap-2 text-sm font-medium">
            <MapPin className="h-4 w-4" />
            {m.personas_filter_location()}
          </label>
          <Select
            value={filters.locationId || "all"}
            onValueChange={handleLocationChange}
            disabled={locationsLoading}
          >
            <SelectTrigger className="bg-background/50 w-full">
              <SelectValue placeholder={m.personas_filter_location_all()} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{m.personas_filter_location_all()}</SelectItem>
              {locations?.map((location) => (
                <SelectItem key={location.id} value={location.id}>
                  {location.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Date Filter */}
        <div className="flex-1 space-y-2">
          <label className="text-muted-foreground flex items-center gap-2 text-sm font-medium">
            <Calendar className="h-4 w-4" />
            {m.personas_filter_date()}
          </label>
          <Select value={filters.date || "all"} onValueChange={handleDateChange}>
            <SelectTrigger className="bg-background/50 w-full">
              <SelectValue placeholder={m.personas_filter_date_placeholder()} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{m.personas_filter_date_placeholder()}</SelectItem>
              <SelectItem value="LAST_7_DAYS">{m.personas_filter_last_7_days()}</SelectItem>
              <SelectItem value="LAST_14_DAYS">{m.personas_filter_last_14_days()}</SelectItem>
              <SelectItem value="LAST_30_DAYS">{m.personas_filter_last_30_days()}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
}
